import React, { useState, useEffect } from 'react';
import { getDatabase, ref, query, orderByChild, equalTo, onValue, off } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import app from '../../services/firebase';
import { Typography, Box, List, ListItem, ListItemText, Divider, Chip } from '@mui/material';
import { ResourceStatus } from '../../datamodels/Resources';
import { Link } from '@mui/material';           // MUI Link

function ResourceListContent() {
    const [publicResources, setPublicResources] = useState([]);
    const [userResources, setUserResources] = useState([]);
    const [adminResources, setAdminResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    
    const database = getDatabase(app);
    const auth = getAuth(app);

    useEffect(() => {
        if (currentUser) {
            currentUser.getIdTokenResult().then((idTokenResult) => {
                setIsAdmin(!!idTokenResult.claims.admin);
            });
        } else {
            setIsAdmin(false);
        }
    }, [currentUser]);

    useEffect(() => {
        // Track current user
        const unsubscribeAuth = auth.onAuthStateChanged((user) => {
            setCurrentUser(user);
        });

        return () => unsubscribeAuth();
    }, [auth]);

    useEffect(() => {
        if (!currentUser && !isAdmin) {
            setLoading(false);
            return;
        }

        const resourcesRef = ref(database, 'resources');
        let activeListeners = [];
        
        if (isAdmin) {
            // Admin: Fetch all resources
            const adminListener = onValue(resourcesRef, (snapshot) => {
                const data = snapshot.val() || {};
                const resourcesWithKeys = Object.entries(data).map(([key, value]) => ({ ...value, id: key }));
                
                // Sort resources: 'proposed' first, then by creation date
                resourcesWithKeys.sort((a, b) => {
                    if (a.status === ResourceStatus.Proposed && b.status !== ResourceStatus.Proposed) return -1;
                    if (a.status !== ResourceStatus.Proposed && b.status === ResourceStatus.Proposed) return 1;
                    return new Date(b.createdAt) - new Date(a.createdAt);
                });

                setAdminResources(resourcesWithKeys);
                setLoading(false);
            }, (err) => { setError(err.message); setLoading(false); });
            activeListeners.push({ ref: resourcesRef, listener: adminListener });
        } else if (currentUser) {
            // Regular user: Fetch public and user-specific resources
            const publicQuery = query(resourcesRef, orderByChild('status'), equalTo(ResourceStatus.ReviewedApproved));
            const publicListener = onValue(publicQuery, (snapshot) => {
                const data = snapshot.val() || {};
                const resourcesWithKeys = Object.entries(data).map(([key, value]) => ({ ...value, id: key }));
                setPublicResources(resourcesWithKeys);
                setLoading(false);
            }, (err) => { setError(err.message); setLoading(false); });
            activeListeners.push({ ref: publicQuery, listener: publicListener });

            if (currentUser.uid) {
                const userQuery = query(resourcesRef, orderByChild('uid'), equalTo(currentUser.uid));
                const userListener = onValue(userQuery, (snapshot) => {
                    const data = snapshot.val() || {};
                    const resourcesWithKeys = Object.entries(data).map(([key, value]) => ({ ...value, id: key }));
                    setUserResources(resourcesWithKeys.filter(r => r.status !== ResourceStatus.ReviewedApproved));
                    setLoading(false);
                }, (err) => { setError(err.message); setLoading(false); });
                activeListeners.push({ ref: userQuery, listener: userListener });
            }
        }

        return () => {
            activeListeners.forEach(({ ref, listener }) => off(ref, 'value', listener));
        };
    }, [database, currentUser, isAdmin]);

    const getVisibleResources = () => {
        if (isAdmin) {
            return adminResources;
        }
        // Combine and remove duplicates for regular users
        const resourceMap = new Map();
        [...publicResources, ...userResources].forEach(resource => {
            resourceMap.set(resource.id, resource);
        });
        return Array.from(resourceMap.values());
    };

    const allResources = getVisibleResources();

    if (loading) {
        return <Typography>Loading resources...</Typography>;
    }

    if (error) {
        return <Typography color="error">Error: {error}</Typography>;
    }
    
    if (!currentUser) {
        return <Typography>Please log in to see resources.</Typography>;
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Resource List
            </Typography>
            {allResources.length > 0 ? (
                <List>
                    {allResources.map((resource) => (
                        <Box key={resource.id}>
                            <ListItem alignItems="flex-start">
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {resource.imageUrl && (
                                            <Box
                                            component="img"
                                            src={resource.imageUrl}
                                            alt="favicon"
                                            sx={{ width: 20, height: 20, borderRadius: '4px' }}
                                            onError={e => { e.target.style.display = 'none'; }}
                                            />
                                        )}
                                        <span>{resource.title}</span>
                                        {(resource.status === ResourceStatus.Proposed || resource.status === 'moderate') ? (
                                            <Chip label="Pending Approval" size="small" color="warning" variant="outlined" />
                                        ) : (resource.visibility === 'public' || resource.status === ResourceStatus.ReviewedApproved) && resource.uid !== 'mobibob' ? (
                                            <Chip label="Public" size="small" color="primary" variant="outlined" />
                                        ) : resource.visibility === 'private' ? (
                                            <Chip label="Private" size="small" variant="outlined" />
                                        ) : null}
                                        </Box>
                                    }
                                    secondary={
                                        <React.Fragment>
                                            <Typography
                                                sx={{ display: 'inline' }}
                                                component="span"
                                                variant="body2"
                                                color="text.primary"
                                            >
                                                {resource.description}
                                            </Typography>
                                            <br />
                                            <Typography
                                                sx={{ display: 'inline' }}
                                                component="span"
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                URL: {resource.url}
                                                <br />
                                                {/* <Link to={`/resources/${resource.id}`}> */}
                                                <Link to={`${resource.url}`}>
                                                    <Typography variant="caption" color="primary">
                                                        View Details
                                                    </Typography>
                                                </Link>
                                            </Typography>
                                        </React.Fragment>
                                    }
                                />
                            </ListItem>
                            <Divider variant="inset" component="li" />
                        </Box>
                    ))}
                </List>
            ) : (
                <Typography>No resources found.</Typography>
            )}
        </Box>
    );
}

export default ResourceListContent;
